import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import { Box, Button, Icon, Loader, Typography } from '@saviynt/design-system';
import {
  type DataProvider,
  type FieldElement,
  type FormValues,
  MetadataForm,
  type MetadataFormRef,
} from '@saviynt/formforge';

import { usePostFormDataMutation } from '../../../features/ChatModule';
import {
  CF_CRT_MED,
  CF_SEC_BLD,
  LIMIT_INLINE_COUNT,
  testIds,
} from '../../constants';
import {
  type ApplicationListResponse,
  type CustomListModalTypes,
} from '../ListViewer';
import UiWrapper, { WRAPPER_MODE } from '../UiWrapper';

import './FormForge.css';

interface FormForgeData {
  ui: FieldElement[];
  dataProviders: Record<string, DataProvider>;
}

interface FormForgeProps {
  id: string;
  isLast?: boolean;
  isFullView?: boolean;
  isRightWindowOpen?: boolean;
  isProcessing?: boolean;
  isChanged?: boolean;
  resetKey?: string;
  isViewing?: boolean;
  currentSelection?: CustomListModalTypes[];
  retentions?: FormValues;
  onInlineExpand?: () => void;
  onExpand?: (retentions?: FormValues) => void;
  onCollapse?: () => void;
  onSubmit?: (formData: FormValues) => void;
}

/**
 * FormForge
 */
function FormForge({
  id,
  isLast,
  isFullView,
  isViewing,
  isRightWindowOpen,
  isProcessing,
  isChanged,
  resetKey,
  currentSelection,
  retentions,
  onInlineExpand,
  onExpand,
  onCollapse,
  onSubmit,
}: FormForgeProps) {
  const formRef = useRef<MetadataFormRef | null>(null);
  const [currentMode, setCurrenMode] = useState(
    isFullView ? WRAPPER_MODE.EXPANDED : WRAPPER_MODE.INLINE
  );
  const [formData, setFormData] = useState<FormForgeData | null>(null);
  const [isFormAvailable, setIsFormAvailable] = useState(true);
  const [inlineItemCount, setInlineItemCount] = useState<number | null>(null);

  const hasExpandedRef = useRef(false);

  const endpointId = useMemo(
    () =>
      (currentSelection as ApplicationListResponse[] | undefined) &&
      (currentSelection as ApplicationListResponse[]).length > 0
        ? (currentSelection as ApplicationListResponse[])[0].applicationId
        : '',
    [currentSelection]
  );

  // API call
  const [fetchFormData, { isError, isLoading }] = usePostFormDataMutation();

  const handleFetchFormData = useCallback(async () => {
    try {
      const controller = fetchFormData({
        url: '/api/ext/forms/dynamicattributes',
        data: {
          endpointKey: Number(endpointId),
          requestType: 'SERVICE_ACCOUNT',
          action: 'CREATE',
        },
        isECMv6: true,
      });
      const response = await controller.unwrap();

      return response;
    } catch {
      throw Error('Error fetching form');
    }
  }, [endpointId, fetchFormData]);

  const triggerFetchFormData = useCallback(() => {
    handleFetchFormData()
      .then((response) => {
        const typedResponse = response as { data: { formData: FormForgeData } };

        if (!('ui' in typedResponse.data.formData)) {
          setIsFormAvailable(false);
        }

        setFormData(typedResponse.data.formData);

        if (typedResponse.data.formData.ui.length > LIMIT_INLINE_COUNT) {
          setInlineItemCount(1);
        }
      })
      .catch((error: string) => {
        Logger.log('Error feching external form: ', error);
      });
  }, [handleFetchFormData]);

  useEffect(() => {
    triggerFetchFormData();
  }, [triggerFetchFormData]);

  // Form utils
  useEffect(() => {
    if (formData && retentions) {
      formRef.current?.setFormValues(retentions).catch(() => {});
    }
  }, [formData, formRef, retentions, currentMode]);

  // Utils
  useEffect(() => {
    if (isFullView) {
      setCurrenMode(WRAPPER_MODE.EXPANDED);

      return;
    }

    if (isViewing) {
      setCurrenMode(WRAPPER_MODE.VIEWING);

      return;
    }

    if (isLast) {
      setCurrenMode(WRAPPER_MODE.INLINE);

      return;
    }

    setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [isFullView, isLast, isViewing, isChanged, resetKey]);

  useEffect(() => {
    if (inlineItemCount !== null && onExpand && !hasExpandedRef.current) {
      setCurrenMode(WRAPPER_MODE.VIEWING);
      setInlineItemCount(null);
      hasExpandedRef.current = true;

      onExpand(retentions);
    }
  }, [inlineItemCount, onExpand, retentions]);

  const handleWrapperExpand = useCallback(() => {
    if (currentMode === WRAPPER_MODE.INLINE_COLLAPSED) {
      setCurrenMode(WRAPPER_MODE.INLINE);

      onInlineExpand?.();

      return;
    }

    if (currentMode === WRAPPER_MODE.INLINE && onExpand) {
      setCurrenMode(WRAPPER_MODE.VIEWING);

      const formValues = formRef.current?.getFormValues();

      onExpand(formValues);

      return;
    }

    setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [currentMode, onExpand, onInlineExpand]);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (isFullView && currentMode === WRAPPER_MODE.EXPANDED && onCollapse) {
        onCollapse();
      }

      if (onSubmit) {
        onSubmit(values);
      }
    },
    [currentMode, isFullView, onCollapse, onSubmit]
  );

  const renderBody = useCallback(() => {
    if (isError) {
      return (
        <Box
          className='ListViewer-errorModal'
          dataTestId={testIds.FORM_FORGE_ERROR}>
          <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
          <Typography kind='body2' color={CF_CRT_MED}>
            <FormattedMessage
              id='apz7lc'
              defaultMessage='System encountered an error. Please contact the Support Administrator'
              description='System encountered an error. Please contact the Support Administrator'
            />
          </Typography>
        </Box>
      );
    }

    if (isLoading || formData === null) {
      return (
        <Box
          tag='div'
          className='FormForge-loaderModal'
          dataTestId={testIds.FORM_FORGE_LOADER}>
          <Loader kind='dots' size='small' format='inline' color='brand' />
        </Box>
      );
    }

    if (isFormAvailable) {
      return (
        <MetadataForm
          ref={formRef}
          canvasFields={formData.ui}
          dataProviders={formData.dataProviders}
          onSubmit={handleSubmit}
          onValuesChange={() => {}}
          logoAlignment='center'
          dataTheme='light'
          dataTestId={testIds.FORM_FORGE_FORM}
          classNames={{ root: 'Savi-FormForge', row: 'Savi-FormForge-row' }}
          isDynamicAttributeForm
        />
      );
    }

    return (
      <Box tag='div' className='CombinedForm-emptyDynamicform'>
        <Typography kind='body2' color={CF_SEC_BLD}>
          <FormattedMessage
            id='SAGZZx'
            defaultMessage='No dynamic attributes are configured'
            description='No dynamic attributes are configured'
          />
        </Typography>
      </Box>
    );
  }, [formData, handleSubmit, isError, isLoading, isFormAvailable]);

  return (
    <UiWrapper
      id={id}
      isActive={Boolean(isLast)}
      isRightWindowOpen={Boolean(isRightWindowOpen)}
      isEdited={isChanged}
      mode={currentMode}
      title='Select Attributes'
      isDisabled={isError || isLoading || isProcessing}
      onExpand={handleWrapperExpand}
      onCollapse={onCollapse}
      onClose={() => {
        setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
      }}
      footer={
        <Box tag='div' className='Form-footerWrapper'>
          <Button
            kind='outlined'
            onClick={() => {
              if (isError || !isFormAvailable) {
                handleSubmit({});

                return;
              }

              formRef.current?.validateAndSubmit().catch(() => {});
            }}
            className='ListViewer-footerActionButton'
            isDisabled={isError || isLoading || isProcessing}
            dataTestId={testIds.FORM_FORGE_CONTINUE_BTN}>
            <FormattedMessage
              id='8jETK0'
              defaultMessage='Continue'
              description='Continue'
            />
          </Button>
        </Box>
      }
      className='FormForge-wrapper'>
      {renderBody()}
    </UiWrapper>
  );
}

FormForge.defaultProps = {
  isLast: true,
  isFullView: false,
  isViewing: false,
  isRightWindowOpen: false,
  isProcessing: false,
  isChanged: false,
  resetKey: '',
  currentSelection: null,
  retentions: null,
  onInlineExpand: undefined,
  onExpand: undefined,
  onCollapse: undefined,
  onSubmit: undefined,
};

export default memo(FormForge);
